import React, { useState, useCallback, useMemo, useEffect } from "react";
import FontAwesome from "react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router";
import { Popup } from "../../components";
import { AddFolderButton } from "./components/AddFolderButton";
import { NodeNameDialog } from "./components/NodeNameDialog";
import { renameFolder } from "../../actions";
import { ROOT_PARENT } from "./utilities/dragUtils";
import { EmptyState } from "./components/EmptyState";
import { Header } from "./components/Header";
import { LoadingState } from "./components/LoadingState";
import { Rows } from "./components/Rows";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useDragState } from "./hooks/useDragState";
import { useInfoNodesTree } from "./hooks/useInfoNodesTree";
import { useDeleteNode } from "./hooks/useDeleteNode";
import { checkLoggedIn, validateAccess } from "../../session";

function InfoPages({ history }) {
	const dispatch = useDispatch();
	const auth = useSelector((state) => state.auth);
	const [editingFolder, setEditingFolder] = useState(null);
	const [isRenamingFolder, setIsRenamingFolder] = useState(false);
	const [isReorderErrorOpen, setIsReorderErrorOpen] = useState(false);
	const site = auth && auth.site ? auth.site : null;
	const canAdmin = useMemo(
		() => (site ? validateAccess(site, "infoPages", auth) : false),
		[site, auth],
	);

	useEffect(() => {
		checkLoggedIn({ props: { history } });
	}, [history]);

	const {
		isLoading,
		displayItems,
		displayNodesById,
		displayNormalizedNodes,
		setOptimisticTree,
		clearOptimisticTree,
		handleReorderNodes,
		handleDeleteNode,
	} = useInfoNodesTree(site);

	/**
	 * Opens the reorder error popup when saving fails.
	 */
	const handleReorderError = useCallback(() => {
		setIsReorderErrorOpen(true);
	}, []);

	/**
	 * Dismisses the reorder error popup.
	 */
	const handleCloseReorderError = useCallback(() => {
		setIsReorderErrorOpen(false);
	}, []);

	const {
		activeId,
		overId,
		isSaving,
		handleDragStart,
		handleDragMove,
		handleDragEnd,
		handleDragCancel,
	} = useDragState({
		items: displayItems,
		nodesById: displayNodesById,
		auth,
		reorderInfoNodes: handleReorderNodes,
		setOptimisticTree,
		clearOptimisticTree,
		onReorderError: handleReorderError,
	});

	/**
	 * Navigates to the page editor, optionally scoping to a folder.
	 * @param {string} [parentId=ROOT_PARENT]
	 */
	const navigateToPageEditor = useCallback(
		(parentId = ROOT_PARENT) => {
			const query =
				parentId && parentId !== ROOT_PARENT ? `?parentId=${parentId}` : "";
			history.push(`/info/infopage${query}`);
		},
		[history],
	);

	const handleCreatePage = useCallback(
		() => navigateToPageEditor(ROOT_PARENT),
		[navigateToPageEditor],
	);

	/**
	 * Triggers a create flow inside a folder.
	 * @param {string} parentId
	 */
	const handleCreatePageInFolder = useCallback(
		(parentId) => {
			if (!parentId) return;
			navigateToPageEditor(parentId);
		},
		[navigateToPageEditor],
	);

	/**
	 * Routes to the edit view for a specific node id.
	 * @param {string} nodeId
	 */
	const handleEditNode = useCallback(
		(nodeId) => {
			if (!nodeId) return;
			history.push(`/info/infopage/${nodeId}`);
		},
		[history],
	);

	/**
	 * Submits a folder rename request.
	 * @param {string} newName
	 * @returns {Promise<void>}
	 */
	const handleFolderRename = async (newName) => {
		if (!editingFolder || !site) return;
		setIsRenamingFolder(true);
		try {
			await dispatch(
				renameFolder({
					site,
					folderId: editingFolder.RowId,
					name: newName,
				}),
			);
			setEditingFolder(null);
		} catch (_err) {
			alert(
				"Something went wrong while renaming the folder. Please try again.",
			);
		} finally {
			setIsRenamingFolder(false);
		}
	};

	/**
	 * Closes the folder rename dialog (unless we're mid-submit).
	 */
	const closeFolderDialog = () => {
		if (!isRenamingFolder) {
			setEditingFolder(null);
		}
	};

	const getNodeById = useCallback(
		(RowId) => displayNodesById.get(RowId),
		[displayNodesById],
	);

	const performDelete = useCallback(
		(node) => {
			if (!site) return;
			return handleDeleteNode({ site, id: node.RowId });
		},
		[site, handleDeleteNode],
	);

	const { promptDelete: handleDeletePrompt, DeleteDialog } = useDeleteNode({
		getNodeById,
		onDelete: performDelete,
	});

	const activeNode = activeId ? displayNodesById.get(activeId) : null;

	const isEmpty = !isLoading && displayNormalizedNodes.length === 0;
	const shouldShowSpinner = isLoading;
	const headerActionsVisible = !isLoading && displayNormalizedNodes.length > 0;
	const loadingMessage = "Retrieving information pages...";

	return (
		<ErrorBoundary>
			<div
				style={{ minWidth: "100%" }}
				className="feature-infopage"
				data-dragging={Boolean(activeId)}
			>
				<div className="infopage__container">
					<Header
						showActions={headerActionsVisible}
						onCreateNewPage={handleCreatePage}
					>
						<AddFolderButton />
					</Header>
					{shouldShowSpinner ? (
						<LoadingState message={loadingMessage} />
					) : isEmpty ? (
						<EmptyState onCreateNewPage={handleCreatePage} />
					) : (
						<Rows
							items={displayItems}
							nodesById={displayNodesById}
							canAdmin={canAdmin}
							activeId={activeId}
							overId={overId}
							activeNode={activeNode}
							onDeleteNode={handleDeletePrompt}
							onEditFolder={setEditingFolder}
							onDragStart={handleDragStart}
							onDragMove={handleDragMove}
							onDragEnd={handleDragEnd}
							onDragCancel={handleDragCancel}
							onCreatePage={canAdmin ? handleCreatePageInFolder : undefined}
							onEditNode={handleEditNode}
						/>
					)}
					{!shouldShowSpinner && isSaving && (
						<div className="rows__saving-wrapper">
							<p className="rows__saving-hint" role="status" aria-live="polite">
								<FontAwesome
									name="spinner"
									className="rows__saving-icon"
									aria-hidden="true"
									spin
								/>
								Saving order…
							</p>
						</div>
					)}
				</div>
				<NodeNameDialog
					isOpen={Boolean(editingFolder)}
					title="Rename folder"
					confirmLabel="Save"
					initialName={editingFolder ? editingFolder.title || "" : ""}
					isSubmitting={isRenamingFolder}
					onConfirm={handleFolderRename}
					onClose={closeFolderDialog}
				/>
				<DeleteDialog />
				{isReorderErrorOpen ? (
					<Popup
						title="Unable to save order"
						onClose={handleCloseReorderError}
						hasPadding
						buttons={[
							{
								text: "OK",
								type: "primary",
								onClick: handleCloseReorderError,
							},
						]}
					>
						<p>Failed to save new order. Please try again.</p>
					</Popup>
				) : null}
			</div>
		</ErrorBoundary>
	);
}

export default withRouter(InfoPages);
